/**
 * This code is copied and modified from https://github.com/Eiskis/bellevue/blob/7156c3f8727eece8b953664cc31ced9501613d52/src/services/viewport.js
 * It provides information about the viewport as a service for other component
 */

import Vue from 'vue';
import * as ViddseeDefaultBreakpoints from './ViewportService/defaultBreakpoint';
import { createViewportProvider, Environment } from './ViewportService/viewportProvider';

export interface ViewportServiceData {
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
}

export interface ViewportServiceBreakpointsOptions {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

export type ViewportServiceBreakpoints = { [K in keyof ViewportServiceBreakpointsOptions]: boolean }

export interface ViewportServiceCompute {
  isScrolled: boolean;
  isScrolledX: boolean;
  isLandscape: boolean;
  isPortrait: boolean;
  breakpoints: ViewportServiceBreakpoints;
}

export interface ViewportService extends ViewportServiceData, ViewportServiceCompute {}

export interface ViewportServiceOptions {
  breakpoints: ViewportServiceBreakpointsOptions;
  environment: Environment;
}

const getOptions = (options: Partial<ViewportServiceOptions> = {}): ViewportServiceOptions => ({
  breakpoints: options.breakpoints || { ...ViddseeDefaultBreakpoints },
  environment: options.environment || window,
});

export const createViewportService = (opts?: Partial<ViewportServiceOptions>): ViewportService => {
  const options = getOptions(opts);
  const environment = createViewportProvider(options.environment);

  const vm = new Vue({
    data(): ViewportServiceData {
      return {
        width: environment.innerWidth,
        height: environment.innerHeight,
        scrollX: environment.pageXOffset,
        scrollY: environment.pageYOffset,
      };
    },

    created(): void {
      environment.addEventListener('resize', this.onResize);
      environment.addEventListener('scroll', this.onScroll);
    },

    beforeDestroy(): void {
      environment.removeEventListener('resize', this.onResize);
      environment.removeEventListener('scroll', this.onScroll);
    },

    methods: {
      // Throttled updaters
      updateDimensions(): void {
        this.width = environment.innerWidth;
        this.height = environment.innerHeight;
      },

      updateScrollValues(): void {
        this.scrollX = environment.pageXOffset;
        this.scrollY = environment.pageYOffset;
      },

      onResize(): void {
        this.updateDimensions();
      },

      onScroll(): void {
        this.updateScrollValues();
      },
    },
  });

  return {
    get width(): number {
      return vm.width;
    },
    get height(): number {
      return vm.height;
    },
    get scrollX(): number {
      return vm.scrollX;
    },
    get scrollY(): number {
      return vm.scrollY;
    },

    get isScrolled(): boolean {
      return vm.scrollY > 0;
    },
    get isScrolledX(): boolean {
      return vm.scrollX > 0;
    },
    get isLandscape(): boolean {
      return vm.width > this.height;
    },
    get isPortrait(): boolean {
      return !this.isLandscape;
    },

    get breakpoints(): ViewportServiceBreakpoints {
      return {
        xs: this.width > options.breakpoints.xs,
        sm: this.width > options.breakpoints.sm,
        md: this.width > options.breakpoints.md,
        lg: this.width > options.breakpoints.lg,
        xl: this.width > options.breakpoints.xl,
      };
    },
  };
};
